import { useLocation } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { LocalizedLink } from '@/components/LocalizedLink';
import { getRouteKeyFromPath, RouteKey } from '@/lib/i18n/routes';
import { getRelatedRoutes } from '@/lib/seo/relatedLinks';
import { getSectionMeta } from '@/lib/seo/sectionMeta';
import type { Language } from '@/lib/i18n/translations-meta';

const HEADING: Record<Language, string> = {
  en: 'Explore more',
  nl: 'Ontdek meer',
  es: 'Explora más',
  de: 'Weitere Themen',
  fr: 'Découvrez aussi',
  it: 'Scopri di più',
  pt: 'Explore mais',
  dk: 'Udforsk mere',
};

interface RelatedLinksProps {
  /** Optional explicit route key. If omitted, resolved from current pathname. */
  routeKey?: RouteKey;
  className?: string;
}

/**
 * Semantic navigation block that surfaces 3–5 thematically related sections.
 * Uses `LocalizedLink` so URLs stay language-correct, and pulls labels from
 * the central `sectionMeta` catalog so link text always matches page titles.
 */
export function RelatedLinks({ routeKey, className }: RelatedLinksProps) {
  const { language } = useLanguage();
  const location = useLocation();
  const resolvedKey = routeKey ?? getRouteKeyFromPath(location.pathname) ?? undefined;
  const related = getRelatedRoutes(resolvedKey);

  if (related.length === 0) return null;

  return (
    <nav
      aria-label={HEADING[language]}
      className={
        className ??
        'container mx-auto px-4 py-12 border-t border-border/40'
      }
    >
      <h2 className="text-xl font-semibold mb-6 text-foreground">
        {HEADING[language]}
      </h2>
      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {related.map((key) => {
          const meta = getSectionMeta(key, language);
          if (!meta) return null;
          // Use the leading portion of the title before " — " / " | " as a
          // concise link label; falls back to the full title.
          const label =
            meta.title.split(/\s[—|]\s/)[0]?.trim() || meta.title;
          return (
            <li key={key}>
              <LocalizedLink
                routeKey={key}
                className="block rounded-lg border border-border/60 bg-card hover:bg-accent/60 transition-colors px-4 py-3 text-sm font-medium text-foreground"
              >
                {label}
              </LocalizedLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export default RelatedLinks;
